import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useRestaurantCache } from '@/context/RestaurantCacheContext';
//import { userStorage } from '@/services/userStorage';

export interface AdminSetupStatus {
  hasRestaurant: boolean;
  isLoading: boolean;
  error: string | null;
  restaurantId?: string;
  needsOnboarding: boolean;
  shouldShowTutorial?: boolean; // Nuevo campo para indicar si mostrar tutorial
}

// Cache para evitar consultas duplicadas
const setupCache = new Map<string, AdminSetupStatus>();

// Bloqueo para evitar consultas simultáneas
let isLoadingInProgress = false;
let currentLoadingPromise: Promise<AdminSetupStatus> | null = null;

// Función para verificar si es la primera vez que el usuario ve el tutorial
const isFirstTimeTutorial = (userId: string, userRole: string): boolean => {
  try {
    const key = `tutorial_shown_${userId}_${userRole}`;
    const hasShown = localStorage.getItem(key);
    return !hasShown;
  } catch (error) {
    console.error('❌ Error verificando primera vez del tutorial:', error);
    return true; // En caso de error, mostrar tutorial
  }
};

// Función para marcar que el tutorial ya fue visto
const markTutorialAsShown = (userId: string, userRole: string): void => {
  try {
    const key = `tutorial_shown_${userId}_${userRole}`;
    localStorage.setItem(key, 'true');
    console.log('✅ Tutorial marcado como visto para:', userId, userRole);
  } catch (error) {
    console.error('❌ Error marcando tutorial como visto:', error);
  }
};

export function useAdminSetup() {
  const { user, userRole } = useAuth();
  const { userRestaurants } = useRestaurantCache();
  const [status, setStatus] = useState<AdminSetupStatus>({
    hasRestaurant: false,
    isLoading: false, // Cambiado a false para no cargar automáticamente
    error: null,
    needsOnboarding: true,
    shouldShowTutorial: false, // Inicialmente no mostrar tutorial
  });
  
  const lastUserId = useRef<string | null>(null);
  const lastUserRole = useRef<string | null>(null);

  // Función para cargar datos bajo demanda
  const loadAdminSetup = useCallback(async () => {
    if (!user) {
      console.log('❌ useAdminSetup - No hay usuario autenticado');
      return {
        hasRestaurant: false,
        isLoading: false,
        error: null,
        needsOnboarding: true,
        shouldShowTutorial: false,
      };
    }

    // Verificar si ya se está cargando para este usuario
    const cacheKey = `${user.uid}-${userRole?.id || 'no-role'}`;
    
    if (setupCache.has(cacheKey)) {
      console.log('📋 useAdminSetup - Usando datos del cache');
      const cachedStatus = setupCache.get(cacheKey)!;
      setStatus(cachedStatus);
      return cachedStatus;
    }

    if (isLoadingInProgress && currentLoadingPromise) {
      console.log('⏳ useAdminSetup - Esperando consulta en progreso...');
      await currentLoadingPromise;
      return setupCache.get(cacheKey) || {
        hasRestaurant: false,
        isLoading: false,
        error: null,
        needsOnboarding: true,
        shouldShowTutorial: false,
      };
    }

    // Iniciar nueva consulta
    isLoadingInProgress = true;
    currentLoadingPromise = (async () => {
      try {
        // Actualizar referencias
        lastUserId.current = user.uid;
        lastUserRole.current = userRole?.id || null;

        console.log('🔄 useAdminSetup - Iniciando verificación de configuración bajo demanda');
        console.log('👤 Usuario:', user.email, 'UID:', user.uid);
        console.log('🎭 Rol del usuario:', userRole?.id, userRole?.name);
        
        setStatus(prev => ({ ...prev, isLoading: true, error: null }));

        // USAR LOS RESTAURANTES YA CARGADOS EN EL CACHE
        let hasRestaurant = false;
        let restaurantId: string | undefined;
        let shouldShowTutorial = false;

        try {
          console.log('🏪 Verificando restaurantes desde cache...');
          
          // Usar los restaurantes ya cargados en el cache
          hasRestaurant = userRestaurants.length > 0;
          console.log('📋 Usuario tiene restaurantes (desde cache):', hasRestaurant, 'Cantidad:', userRestaurants.length);

          // Lógica para determinar si mostrar tutorial:
          // 1. Si no hay restaurantes, verificar si es la primera vez
          // 2. Si es la primera vez que el usuario owner inicia sesión, mostrar tutorial
          if (!hasRestaurant) {
            // Verificar si es la primera vez que no hay restaurantes
            const isFirstTimeNoRestaurants = isFirstTimeTutorial(user.uid, 'no-restaurants');
            if (isFirstTimeNoRestaurants) {
              console.log('🎯 Primera vez sin restaurantes - Activando tutorial automáticamente');
              shouldShowTutorial = true;
            } else {
              console.log('✅ Usuario ya vio el tutorial sin restaurantes - No mostrar automáticamente');
              shouldShowTutorial = false;
            }
          } else if (userRole?.id === 'owner') {
            // Para usuarios owner, verificar si es la primera vez
            const isFirstTime = isFirstTimeTutorial(user.uid, userRole.id);
            if (isFirstTime) {
              console.log('🎯 Primera vez del usuario owner - Activando tutorial automáticamente');
              shouldShowTutorial = true;
            } else {
              console.log('✅ Usuario owner ya vio el tutorial anteriormente');
              shouldShowTutorial = false;
            }
          } else {
            console.log('✅ Usuario no es owner o ya tiene restaurantes - No mostrar tutorial');
            shouldShowTutorial = false;
          }
          
          console.log('✅ Resultado de verificación de restaurantes:', {
            hasRestaurant,
            shouldShowTutorial,
            userRole: userRole?.id,
            isFirstTime: userRole?.id === 'owner' ? isFirstTimeTutorial(user.uid, userRole.id) : false
          });

          // Log detallado de la decisión del tutorial
          console.log('🔍 Decisión del tutorial:', {
            hasRestaurant,
            userRole: userRole?.id,
            isFirstTimeNoRestaurants: !hasRestaurant ? isFirstTimeTutorial(user.uid, 'no-restaurants') : 'N/A',
            isFirstTimeOwner: userRole?.id === 'owner' ? isFirstTimeTutorial(user.uid, userRole.id) : 'N/A',
            shouldShowTutorial,
            reason: !hasRestaurant 
              ? (isFirstTimeTutorial(user.uid, 'no-restaurants') ? 'Primera vez sin restaurantes' : 'Ya vio tutorial sin restaurantes')
              : (userRole?.id === 'owner' 
                ? (isFirstTimeTutorial(user.uid, userRole.id) ? 'Primera vez del owner' : 'Owner ya vio tutorial')
                : 'No es owner o tiene restaurantes')
          });

        } catch (error) {
          console.error('❌ Error verificando restaurantes:', error);
          hasRestaurant = false;
          // En caso de error, también mostrar tutorial
          shouldShowTutorial = true;
        }

        // Determinar si necesita onboarding basado únicamente en si hay restaurantes
        const needsOnboarding = !hasRestaurant;

        const finalStatus = {
          hasRestaurant,
          isLoading: false,
          error: null,
          restaurantId,
          needsOnboarding,
          shouldShowTutorial,
        };

        console.log('🎯 Estado final de configuración:', finalStatus);
        console.log('🔍 Estado final del tutorial:', {
          shouldShowTutorial: finalStatus.shouldShowTutorial,
          hasRestaurant: finalStatus.hasRestaurant,
          userRole: userRole?.id,
          userId: user.uid
        });

        // Guardar en cache
        setupCache.set(cacheKey, finalStatus);
        
        setStatus(finalStatus);
        return finalStatus;

      } catch (error) {
        console.error('❌ Error general en useAdminSetup:', error);
        const errorStatus = {
          hasRestaurant: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Error desconocido',
          needsOnboarding: true,
          shouldShowTutorial: true, // En caso de error, mostrar tutorial
        };
        
        // Guardar error en cache también
        setupCache.set(cacheKey, errorStatus);
        setStatus(errorStatus);
        return errorStatus;
      } finally {
        // Limpiar estado de carga
        isLoadingInProgress = false;
        currentLoadingPromise = null;
      }
    })();

    // Esperar a que termine la consulta
    await currentLoadingPromise;
  }, [user, userRole, userRestaurants]);

  // Solo cargar automáticamente si no hay datos en cache
  useEffect(() => {
    if (!user) {
      setStatus(prev => ({ 
        ...prev, 
        isLoading: false,
        needsOnboarding: true,
        shouldShowTutorial: false
      }));
      return;
    }

    const cacheKey = `${user.uid}-${userRole?.id || 'no-role'}`;
    
    // Solo cargar automáticamente si no hay datos en cache Y estamos en el dashboard
    const isDashboard = window.location.pathname === '/admin';
    
    if (!setupCache.has(cacheKey) && isDashboard) {
      console.log('🚀 Cargando datos automáticamente (dashboard)');
      loadAdminSetup();
    } else if (setupCache.has(cacheKey)) {
      console.log('📋 Usando datos del cache (carga automática)');
      setStatus(setupCache.get(cacheKey)!);
    } else {
      console.log('⏸️ No cargando datos automáticamente (no dashboard)');
    }

    // Log para verificar estado del tutorial en localStorage
    if (user?.uid && userRole?.id) {
      // Verificar tanto el rol del usuario como el estado "no-restaurants"
      const userRoleKey = `tutorial_shown_${user.uid}_${userRole.id}`;
      const noRestaurantsKey = `tutorial_shown_${user.uid}_no-restaurants`;
      
      const userRoleShown = localStorage.getItem(userRoleKey);
      const noRestaurantsShown = localStorage.getItem(noRestaurantsKey);
      
      console.log('🔍 Estado del tutorial en localStorage:', {
        userRoleKey,
        userRoleShown,
        noRestaurantsKey,
        noRestaurantsShown,
        isFirstTimeUserRole: !userRoleShown,
        isFirstTimeNoRestaurants: !noRestaurantsShown,
        userId: user.uid,
        userRole: userRole.id
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userRole]); // Removido loadAdminSetup de las dependencias

  return {
    ...status,
    needsOnboarding: status.needsOnboarding,
    shouldShowTutorial: status.shouldShowTutorial,
    loadAdminSetup, // Exponer función para carga manual
    markTutorialAsShown: () => {
      if (user?.uid && userRole?.id) {
        // Marcar como visto según el contexto
        if (!status.hasRestaurant) {
          // Si no hay restaurantes, marcar como visto para "no-restaurants"
          markTutorialAsShown(user.uid, 'no-restaurants');
          console.log('✅ Tutorial marcado como visto para usuario sin restaurantes');
        } else {
          // Si hay restaurantes, marcar como visto para el rol del usuario
          markTutorialAsShown(user.uid, userRole.id);
          console.log('✅ Tutorial marcado como visto para usuario con restaurantes');
        }
        
        // Actualizar el estado local
        setStatus(prev => ({ ...prev, shouldShowTutorial: false }));
        // Actualizar el cache
        const cacheKey = `${user.uid}-${userRole.id}`;
        if (setupCache.has(cacheKey)) {
          const cached = setupCache.get(cacheKey)!;
          setupCache.set(cacheKey, { ...cached, shouldShowTutorial: false });
        }
        
        // Log adicional para verificar que se marcó como visto
        const tutorialKey = !status.hasRestaurant ? `tutorial_shown_${user.uid}_no-restaurants` : `tutorial_shown_${user.uid}_${userRole.id}`;
        const tutorialShown = localStorage.getItem(tutorialKey);
        console.log('✅ Tutorial marcado como visto - Verificación localStorage:', {
          key: tutorialKey,
          tutorialShown: tutorialShown,
          shouldShowTutorial: false,
          context: !status.hasRestaurant ? 'no-restaurants' : 'user-role'
        });
      }
    },
  };
} 